import { useMemo } from 'react';

import type { ScheduleStore } from '@nucleus-commerce/api-client/admin';

import { session } from '@/shared/session/session';

import type { StoreOption } from '../components/schedule-stores-field';

/**
 * Stores a schedule can target: the member's own stores, plus any current target they cannot
 * reach (shown locked, since every write needs access to every target).
 */
export function useStoreOptions(targets: readonly ScheduleStore[] = []): StoreOption[] {
  const { me } = session.useSession();
  return useMemo(() => {
    const own = (me?.stores ?? []).map((store): StoreOption => ({
      id: store.id,
      name: store.name,
      timezone: store.timezone,
      closed: store.status === 'closed',
      locked: false,
    }));
    const known = new Set(own.map((s) => s.id));
    const foreign = targets
      .filter((target) => !known.has(target.storeId))
      .map((target): StoreOption => ({
        id: target.storeId,
        name: target.storeName,
        timezone: target.storeTimeZone,
        closed: false,
        locked: true,
      }));
    return [...own, ...foreign];
  }, [me?.stores, targets]);
}
